import { Component, OnDestroy, OnInit } from '@angular/core';
import { map, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseIndexPageComponent } from '../../../../components/pages/base-index-page/base-index-page.component';
import BaseIndexPageConfig from '../../../../components/pages/base-index-page/base-index-page.config';
import { DataTableColumnActions } from '../../../../components/widgets/data-table/columns/data-table-column-actions/data-table-column-actions.config';
import { DataTableColumnText } from '../../../../components/widgets/data-table/columns/data-table-column-text/data-table-column-text.config';

import ApiOptionsConfig from '../../../../services/api/api-options.config';
import { QualityManagementService } from '../../../../services/quality-management.service';
import { RouteService } from '../../../../services/route.service';
import { MainLayoutService } from '../../../../services/main-layout.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { CommonService } from '../../../../services/common.service';

import {
  MeetingMinute,
  meetingMinuteDepartmentLabel,
  meetingMinuteUserFullName
} from '../../../../models/entities/meeting-minute';

@Component({
  selector: 'app-meeting-minutes-index-page',
  imports: [BaseIndexPageComponent],
  templateUrl: './meeting-minutes-index-page.component.html',
  styleUrl: './meeting-minutes-index-page.component.scss',
})
export class MeetingMinutesIndexPageComponent implements OnInit, OnDestroy {

  baseIndexPageConfig: BaseIndexPageConfig;

  private activeProfileCode: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    protected qualityManagementService: QualityManagementService,
    private routeService: RouteService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService,
    private commonService: CommonService
  ) {
    this.baseIndexPageConfig = {
      pageTitle: 'Meeting Minutes',
      cardTitle: 'Meeting Minute',
      dataTable: {
        data: [],
        searchModel: {
          subject: null,
          date: null,
          place: null,
          department: null,
        },
        pagination: null,
        columns: [
          new DataTableColumnText({
            attribute: 'subject',
            header: 'Subject',
            filter: 'text',
            value: (item: MeetingMinute) => item?.subject ?? ''
          }),
          new DataTableColumnText({
            attribute: 'date',
            header: 'Date',
            filter: 'date',
            value: (item: MeetingMinute) => this.formatDate(item?.date)
          }),
          new DataTableColumnText({
            attribute: 'place',
            header: 'Place',
            filter: 'text',
            value: (item: MeetingMinute) => item?.place ?? ''
          }),
          new DataTableColumnText({
            attribute: 'department',
            header: 'Department',
            filter: 'select',
            filterOptionsSource: 'meetingMinuteDepartments',
            value: (item: MeetingMinute) => meetingMinuteDepartmentLabel(item?.department) || '-'
          }),
          new DataTableColumnText({
            attribute: 'writtenByUser',
            header: 'Written by',
            sortable: false,
            value: (item: MeetingMinute) => meetingMinuteUserFullName(item?.writtenByUser) || '-'
          }),
          new DataTableColumnText({
            attribute: 'reviewedByUser',
            header: 'Reviewed by',
            sortable: false,
            value: (item: MeetingMinute) => meetingMinuteUserFullName(item?.reviewedByUser) || '-'
          }),
          new DataTableColumnActions({
            header: 'Actions',
            attribute: 'actions',
            style: { width: '140px' },
            buttons: [
              {
                icon: 'fa-eye',
                tooltip: 'View meeting minute details',
                onClick: (item: MeetingMinute) => {
                  this.routeService.meetingMinutesDetail(Number(item.meetingMinuteId));
                }
              },
              {
                icon: 'fa-edit',
                tooltip: 'Edit meeting minute',
                onClick: (item: MeetingMinute) => {
                  this.routeService.meetingMinutesUpdate(Number(item.meetingMinuteId));
                },
                hidden: () => !this.canModify()
              },
              {
                icon: 'fa-trash',
                tooltip: 'Delete meeting minute',
                onClick: () => { },
                isDelete: true,
                hidden: () => !this.canDelete()
              },
            ]
          })
        ]
      },
      apiListService: (options: ApiOptionsConfig) => this.qualityManagementService.listMeetingMinutes(options).pipe(
        map((response: any) => ({
          ...response,
          data: {
            meetingMinutes: this.qualityManagementService.readResponseKey(response, 'meetingMinutes') ?? []
          },
          pagination: response?.pagination ?? response?.data?.pagination ?? null
        }))
      ),
      responseListModelName: 'meetingMinutes',
    }
  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$
      .pipe(takeUntil(this.destroy$))
      .subscribe(profileCode => {
        this.activeProfileCode = profileCode;

        if (this.canModify()) {
          this.baseIndexPageConfig.addNewItemButton = {
            type: 'success',
            title: 'New Meeting Minute',
            onClick: () => this.routeService.meetingMinutesCreate()
          };
        } else {
          this.baseIndexPageConfig.addNewItemButton = undefined;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private formatDate(value?: string | null): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? '-' : this.commonService.formatDateOnly(date);
  }

  private canModify(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_MEETING_MINUTES',
      'modify'
    );
  }

  private canDelete(): boolean {
    return this.accessControlService.hasFeatureActionAccess(
      this.activeProfileCode,
      'QUALITY_MANAGEMENT_MEETING_MINUTES',
      'delete'
    );
  }
}
